import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { GetSubscriptionsCall } from "../../../redux/api/subscription.api";
import { useExchangeRates } from "hooks/useExchngeRates";

const SUBSCRIPTION_TABS = [
  { value: "SUBSCRIBED", label: "New", fullLabel: "New subscribers" },
  { value: "RENEWED", label: "Renews", fullLabel: "Renews" },
  { value: "UNSUBSCRIBED", label: "Cancelled", fullLabel: "Cancelled" },
];

function SubscriptionTabBar({ activeTab, onChange, tabTotals, totalRows, loading }) {
  const theme = useTheme();

  const badgeFor = (value) => {
    if (value === "UNSUBSCRIBED") return tabTotals?.cancelled ?? null;
    if (value === activeTab) return totalRows ?? null;
    return null;
  };

  return (
    <Box
      role="tablist"
      aria-label="Subscription filters"
      sx={{
        display: "flex",
        gap: 0.75,
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        pb: 0.25,
      }}
    >
      {SUBSCRIPTION_TABS.map((tab) => {
        const selected = activeTab === tab.value;
        const badge = badgeFor(tab.value);

        return (
          <Box
            key={tab.value}
            component="button"
            type="button"
            role="tab"
            aria-selected={selected}
            aria-label={tab.fullLabel}
            disabled={loading}
            onClick={() => onChange(tab.value)}
            sx={{
              appearance: "none",
              WebkitTapHighlightColor: "transparent",
              border: "none",
              outline: "none",
              flexShrink: 0,
              cursor: loading ? "wait" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              minHeight: 36,
              px: 1.75,
              py: 0.625,
              borderRadius: 999,
              fontFamily: "inherit",
              fontSize: "0.8125rem",
              fontWeight: selected ? 600 : 500,
              lineHeight: 1.2,
              color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
              bgcolor: selected
                ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.1)
                : "transparent",
              transition: "color 0.15s ease, background-color 0.15s ease",
              "&:hover": {
                bgcolor: selected
                  ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.14)
                  : alpha(theme.palette.text.primary, 0.05),
                color: selected ? theme.palette.primary.main : theme.palette.text.primary,
              },
              "&:focus-visible": {
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`,
              },
              "&:disabled": { opacity: 0.6 },
            }}
          >
            <span>{tab.label}</span>
            {badge != null ? (
              <Box
                component="span"
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  lineHeight: 1,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 999,
                  color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
                  bgcolor: selected
                    ? alpha(theme.palette.primary.main, 0.14)
                    : alpha(theme.palette.text.primary, 0.06),
                }}
              >
                {badge.toLocaleString()}
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

function SubscriptionDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const searchDebounceRef = useRef(null);
  const [activeTab, setActiveTab] = useState("SUBSCRIBED");
  const [lastUpdated, setLastUpdated] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);
  const [tabTotals, setTabTotals] = useState({});
  const [performance, setPerformance] = useState({});
  const [warnings, setWarnings] = useState([]);

  const { rates, error: ratesError } = useExchangeRates();

  const fetchSubscriptions = useCallback(
    async (pageNumber = 0, pageSize = 25, status = "SUBSCRIBED", query = "") => {
      setLoading(true);
      setError("");
      setWarnings([]);

      try {
        const offset = pageNumber * pageSize;
        const response = await GetSubscriptionsCall({
          limit: pageSize,
          offset,
          status,
          search: query,
          concurrency: 8,
        });

        const data = response.data || [];
        setSubscriptions(data);
        setTotalRows(response.total || 0);
        setTabTotals(response.tab_totals || {});
        setPerformance(response.performance || {});
        setWarnings(response.warnings || []);
        setLastUpdated(new Date());
      } catch (err) {
        setError(err?.message || "Failed to load subscription data");
        setSubscriptions([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(searchTerm.trim());
      setPage(0);
    }, 350);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchSubscriptions(page, rowsPerPage, activeTab, searchQuery);
  }, [page, rowsPerPage, activeTab, searchQuery, fetchSubscriptions]);

  const handleTabChange = (nextTab) => {
    if (!nextTab || nextTab === activeTab) return;
    setActiveTab(nextTab);
    setPage(0);
    setSearchTerm("");
    setSearchQuery("");
  };

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchSubscriptions(page, rowsPerPage, activeTab, searchQuery);
  };

  const getCountryFlag = (countryCode) => {
    if (!countryCode) return null;
    const code = countryCode.toUpperCase();
    if (code.length !== 2) return null;
    const codePoints = [...code].map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  const convertToUSD = useCallback(
    (price, currency) => {
      if (!price || price === "N/A" || !currency) return null;
      const currencyUpper = currency.toUpperCase();
      if (currencyUpper === "USD") return Number(price);
      const rate = rates[currencyUpper];
      if (!rate) return null;
      return Number(price) * rate;
    },
    [rates],
  );

  const formatPrice = useCallback(
    (price, currency) => {
      if (price === "N/A" || !price) return "N/A";
      const numPrice = Number(price);
      const currencyUpper = currency?.toUpperCase() || "USD";
      if (currencyUpper === "USD") return `$${numPrice.toFixed(2)}`;
      const usdPrice = convertToUSD(price, currency);
      if (usdPrice === null) {
        return `${currencyUpper} ${numPrice.toFixed(2)}`;
      }
      return `$${usdPrice.toFixed(2)} (${currencyUpper} ${numPrice.toFixed(2)})`;
    },
    [convertToUSD],
  );

  const getSubscriptionDetails = (sub) => {
    const subscriptionMap = sub.subscriptions || {};
    const now = Date.now();
    let subscription = null;
    let subscriptionKey = null;

    for (const [key, entry] of Object.entries(subscriptionMap)) {
      const expires = entry?.expires_date ? new Date(entry.expires_date).getTime() : 0;
      if (!expires || expires <= now) continue;
      if (!subscription || expires > new Date(subscription.expires_date).getTime()) {
        subscription = entry;
        subscriptionKey = key;
      }
    }

    if (!subscription) {
      const fallbackKey = Object.keys(subscriptionMap)[0];
      subscription = fallbackKey ? subscriptionMap[fallbackKey] : null;
      subscriptionKey = fallbackKey;
    }

    if (!subscription) {
      return {
        packageName: sub.product_identifier || "N/A",
        price: "N/A",
        currency: "",
        expiresDate: null,
        purchaseDate: sub.purchase_date || sub.latest_purchase_at || null,
      };
    }

    const entitlements = sub.entitlements || {};
    const entitlementKey = Object.keys(entitlements)[0];
    return {
      packageName: entitlementKey || subscription.display_name || sub.product_identifier || "N/A",
      price: subscription.price?.amount || "N/A",
      currency: subscription.price?.currency || "",
      expiresDate: subscription.expires_date || null,
      purchaseDate:
        sub.purchase_date ||
        sub.latest_purchase_at ||
        subscription.purchase_date ||
        subscription.original_purchase_date ||
        null,
    };
  };

  const stats = useMemo(
    () => ({
      activeSubscribers: tabTotals.active_subscribers ?? 0,
      cancelled: tabTotals.cancelled ?? 0,
      showing: subscriptions.length,
    }),
    [tabTotals, subscriptions.length],
  );

  const bodyCellSx = { py: 1.5, px: 2 };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusStyles = {
    SUBSCRIBED: { color: "success", label: "New subscriber" },
    RENEWED: { color: "info", label: "Renewal" },
    UNSUBSCRIBED: { color: "default", label: "Cancelled" },
  };

  const PlatformIcon = ({ platform }) => {
    if (!platform) {
      return <Chip size="small" label="N/A" variant="outlined" color="default" />;
    }
    if (platform.toLowerCase() === "ios") {
      return (
        <Avatar sx={{ width: 32, height: 32, bgcolor: "#000" }}>
          <AppleIcon sx={{ fontSize: 20, color: "#fff" }} />
        </Avatar>
      );
    }
    if (platform.toLowerCase() === "android") {
      return (
        <Avatar sx={{ width: 32, height: 32, bgcolor: "#3DDC84" }}>
          <AndroidIcon sx={{ fontSize: 20, color: "#fff" }} />
        </Avatar>
      );
    }
    return (
      <Chip size="small" label={platform.toUpperCase()} variant="outlined" color="default" />
    );
  };

  const isCancelledTab = activeTab === "UNSUBSCRIBED";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 2, md: 3 }} px={{ xs: 1.5, md: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "flex-start" }}
          mb={3}
        >
          <Box>
            <MDTypography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
              Subscription Dashboard
            </MDTypography>
            <MDTypography variant="body2" color="text" sx={{ mt: 0.5, maxWidth: 560 }}>
              Fast DB-backed lists — RevenueCat enriches only the active page of subscribers.
            </MDTypography>
            {lastUpdated && (
              <MDTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                Last updated: {formatDate(lastUpdated)}
              </MDTypography>
            )}
          </Box>
          <MDButton
            color="info"
            variant="contained"
            onClick={handleRefresh}
            disabled={loading}
            fullWidth={isMobile}
            sx={{ flexShrink: 0, minHeight: 44 }}
          >
            <Icon sx={{ mr: 1 }}>{loading ? "hourglass_top" : "refresh"}</Icon>
            {loading ? "Refreshing..." : "Refresh"}
          </MDButton>
        </Stack>

        {ratesError && (
          <MDBox mb={3}>
            <Alert severity="warning">
              Exchange rates unavailable. Prices will show original currency only.
            </Alert>
          </MDBox>
        )}

        {warnings.length > 0 && (
          <MDBox mb={3}>
            <Alert severity="warning">
              {warnings.length} user(s) failed to load from RevenueCat on this page
            </Alert>
          </MDBox>
        )}

        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Active subscribers"
              value={stats.activeSubscribers}
              icon="trending_up"
              color="success"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Cancelled / lapsed"
              value={stats.cancelled}
              icon="trending_down"
              color="secondary"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard title="On this page" value={stats.showing} icon="visibility" color="info" />
          </Grid>
        </Grid>

        <Card
          variant="outlined"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: "none",
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
            <Box
              sx={{
                pb: 1.5,
                mb: 1.5,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <SubscriptionTabBar
                activeTab={activeTab}
                onChange={handleTabChange}
                tabTotals={tabTotals}
                totalRows={totalRows}
                loading={loading}
              />
            </Box>

            <Stack spacing={1.25}>
              <TextField
                fullWidth
                size="small"
                label="Search by email or name"
                placeholder="e.g. john@… or Maria"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: 40,
                    borderRadius: 1.5,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: { xs: "left", md: "right" } }}
              >
                {searchQuery
                  ? `Search “${searchQuery}” · ${totalRows} match${totalRows === 1 ? "" : "es"}`
                  : `Page ${page + 1} · ${totalRows.toLocaleString()} in this tab`}
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        {error && (
          <MDBox mt={2}>
            <Alert severity="error" action={<MDButton onClick={handleRefresh}>Retry</MDButton>}>
              {error}
            </Alert>
          </MDBox>
        )}

        <Card
          sx={{
            mt: 2,
            borderRadius: { xs: 2.5, sm: 3 },
            overflow: "hidden",
          }}
        >
          <CardHeader
            title={SUBSCRIPTION_TABS.find((t) => t.value === activeTab)?.fullLabel || "Subscriptions"}
            subheader={
              isCancelledTab
                ? "Cancellation surveys + past purchases"
                : "Sorted by latest purchase"
            }
            titleTypographyProps={{
              variant: isMobile ? "subtitle1" : "h6",
              fontWeight: 700,
            }}
            subheaderTypographyProps={{ variant: "caption" }}
            sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}
          />
          <Divider />

          {loading ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress size={28} />
              <MDTypography variant="button" color="text" ml={2}>
                Loading subscriptions...
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <Box sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="center">Platform</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>{isCancelledTab ? "Reason" : "Package"}</TableCell>
                      <TableCell>{isCancelledTab ? "Cancelled" : "Price"}</TableCell>
                      <TableCell>{isCancelledTab ? "Last purchase" : "Purchase date"}</TableCell>
                      {!isCancelledTab && <TableCell>Expires</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subscriptions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isCancelledTab ? 6 : 7} align="center" sx={{ py: 4 }}>
                          <MDTypography variant="button" color="text">
                            {searchQuery
                              ? "No subscriptions match your search"
                              : "No subscription data for this tab"}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      subscriptions.map((sub) => {
                        const details = getSubscriptionDetails(sub);
                        const userId = sub.user_id ?? sub.id ?? sub.userId;
                        const countryFlag = getCountryFlag(sub.country);

                        return (
                          <TableRow key={userId || sub.email || sub.name} hover>
                            <TableCell sx={{ ...bodyCellSx, minWidth: 240 }} align="left">
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box component="span" sx={{ fontSize: "1.5rem", lineHeight: 1 }}>
                                  {countryFlag || "🌍"}
                                </Box>
                                <Box>
                                  <MDTypography variant="button" fontWeight="medium" display="block">
                                    {sub.name || "Unknown User"}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text" display="block">
                                    {sub.email || "No email"}
                                  </MDTypography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={bodyCellSx} align="center">
                              <Box display="flex" justifyContent="center">
                                <PlatformIcon platform={sub.platform} />
                              </Box>
                            </TableCell>
                            <TableCell sx={bodyCellSx} align="left">
                              <Chip
                                size="small"
                                label={statusStyles[sub.status]?.label || sub.status || "Unknown"}
                                color={statusStyles[sub.status]?.color || "default"}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ ...bodyCellSx, minWidth: 180 }} align="left">
                              <MDTypography variant="caption" fontWeight="medium">
                                {isCancelledTab
                                  ? sub.cancellation_reason || "—"
                                  : details.packageName}
                              </MDTypography>
                            </TableCell>
                            <TableCell sx={{ ...bodyCellSx, minWidth: 140 }} align="left">
                              <MDTypography variant="caption" fontWeight="medium" color="text">
                                {isCancelledTab
                                  ? formatDate(sub.cancelled_at)
                                  : formatPrice(details.price, details.currency)}
                              </MDTypography>
                            </TableCell>
                            <TableCell sx={bodyCellSx} align="left">
                              <MDTypography variant="caption" color="text">
                                {formatDate(
                                  isCancelledTab
                                    ? sub.purchase_date || sub.latest_purchase_at
                                    : details.purchaseDate || sub.purchase_date || sub.latest_purchase_at,
                                )}
                              </MDTypography>
                            </TableCell>
                            {!isCancelledTab && (
                              <TableCell sx={bodyCellSx} align="left">
                                <MDTypography variant="caption" color="text">
                                  {formatDate(details.expiresDate)}
                                </MDTypography>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Box>

              <TablePagination
                component="div"
                count={totalRows}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={isMobile ? [10, 25, 50] : [10, 25, 50, 100]}
                labelRowsPerPage={isMobile ? "Rows" : "Rows per page"}
                sx={{
                  px: { xs: 0.5, sm: 2 },
                  ".MuiTablePagination-toolbar": {
                    flexWrap: "wrap",
                    gap: 1,
                    minHeight: { xs: 52, sm: 64 },
                  },
                  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  },
                }}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
              />
            </>
          )}
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

const StatCard = ({ title, value, icon, color }) => (
  <Card>
    <CardContent>
      <MDBox display="flex" justifyContent="space-between" alignItems="center">
        <div>
          <MDTypography variant="button" color="text">
            {title}
          </MDTypography>
          <MDTypography variant="h4" fontWeight="medium">
            {value}
          </MDTypography>
        </div>
        <MDBox
          display="grid"
          alignItems="center"
          justifyContent="center"
          sx={(theme) => ({
            width: 60,
            height: 60,
            borderRadius: theme.borders.borderRadius.md,
            backgroundColor: theme.palette[color]?.light || theme.palette.info.light,
            color: theme.palette[color]?.main || theme.palette.info.main,
          })}
        >
          <Icon>{icon}</Icon>
        </MDBox>
      </MDBox>
    </CardContent>
  </Card>
);

export default SubscriptionDashboard;
